import { Table, ActionIcon, Menu } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';

import { useUpdateUser } from '../hooks/use-update-user';
import { useDeleteUser } from '../hooks/use-delete-user';
import { convertUsersToTable } from '../utils/helpers/converter';
import { userRoleTitles } from '@/shared/utils/helpers/common';
import { CustomTable, CustomTableMobile, useResponsive } from '@/shared';
import { EditUserForm } from './edit-user-form';

import type { TUser } from '../store/types';

type Props = {
  items: TUser[];
  isLoading?: boolean;
  itemsCount?: number;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
};

export const UsersTable = (props: Props) => {
  const { items } = props;
  const { isDesktop } = useResponsive();

  const {
    handleUserEdit,
    openEditModal,
  } = useUpdateUser();

  const {
    openDeleteModal,
  } = useDeleteUser();

  const getActions = (item: TUser) => (
    <Menu position="bottom-end" shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="subtle">
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconEdit size={14} />}
          onClick={() => openEditModal(item, (
            <EditUserForm
              user={item}
              onSubmit={handleUserEdit}
              onClose={modals.closeAll}
            />
          ))}
        >
          Редактировать
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() => openDeleteModal(item)}
        >
          Удалить
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const titles = [
    'ID',
    'Имя',
    'Email',
    'Роль',
    'ID партнёра',
    'Создан',
  ];

  const rowsDesktop = convertUsersToTable(items).map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.id}</Table.Td>
      <Table.Td>{item.name}</Table.Td>
      <Table.Td>{item.email}</Table.Td>
      <Table.Td>{userRoleTitles[item.role] ?? item.role}</Table.Td>
      <Table.Td>{item.partnerId ?? '-'}</Table.Td>
      <Table.Td>{item.created}</Table.Td>
      <Table.Td width={50}>{getActions(item)}</Table.Td>
    </Table.Tr>
  ));

  const rowsMobile = convertUsersToTable(items).map((item) => ({
    key: item.id,
    headerActions: getActions(item),
    items: [
      { name: titles[0], value: item.id },
      { name: titles[1], value: item.name },
      { name: titles[2], value: item.email },
      { name: titles[3], value: userRoleTitles[item.role] ?? item.role },
      { name: titles[4], value: item.partnerId ?? '-' },
      { name: titles[5], value: item.created },
    ],
  }));

  return isDesktop
    ? (
      <CustomTable
        titles={titles}
        rows={rowsDesktop}
        {...props}
      />
    )
    : (
      <CustomTableMobile
        rows={rowsMobile}
        headerPointsCount={2}
        {...props}
      />
    );
};
